import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Users, ShieldCheck, Check, X as XIcon, AlertTriangle, Clock,
  RefreshCw, Search, ArrowUpRight, Mail, Globe, Award,
} from "lucide-react";

/**
 * /app/admin/partners — HQ admin review queue (hq_admin only).
 *
 * Phase A capability: list applications + stats, open an application,
 * issue decision (approve/decline/info/waitlist) with risk level,
 * tier, and territory. On approve: a partner row is materialized and
 * the matching user account auto-elevates to the ``partner`` role.
 */

const STATUS_META = {
  submitted: { label: "Submitted", tone: "border-[#E8D6A3]/40 bg-[#E8D6A3]/[0.05] text-[#E8D6A3]" },
  triage: { label: "In triage", tone: "border-[#E8D6A3]/40 bg-[#E8D6A3]/[0.05] text-[#E8D6A3]" },
  discovery: { label: "Discovery", tone: "border-[#00E5FF]/40 bg-[#00E5FF]/[0.05] text-[#00E5FF]" },
  due_diligence: { label: "Due diligence", tone: "border-[#00E5FF]/40 bg-[#00E5FF]/[0.05] text-[#00E5FF]" },
  decision_review: { label: "Decision review", tone: "border-[#D4B878]/40 bg-[#D4B878]/[0.05] text-[#D4B878]" },
  info_requested: { label: "Info requested", tone: "border-[#E8D6A3]/40 bg-[#E8D6A3]/[0.05] text-[#E8D6A3]" },
  waitlisted: { label: "Waitlisted", tone: "border-white/15 bg-white/[0.03] text-white/70" },
  approved: { label: "Approved", tone: "border-[#CCFF00]/40 bg-[#CCFF00]/[0.05] text-[#CCFF00]" },
  active: { label: "Active", tone: "border-[#CCFF00]/40 bg-[#CCFF00]/[0.05] text-[#CCFF00]" },
  declined: { label: "Declined", tone: "border-red-400/40 bg-red-500/[0.05] text-red-200" },
};

export default function AdminPartners() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("submitted");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null); // full application
  const [decision, setDecision] = useState({
    decision: "approve",
    risk_level: "standard",
    assigned_tier: "registered",
    assigned_territory: "",
    reviewer_note: "",
  });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const q = filter !== "all" ? `?status=${filter}&limit=200` : "?limit=200";
    const r = await api.get(`/admin/partner-applications${q}`);
    setData(r.data);
  };

  useEffect(() => { load().catch(() => toast.error("Couldn't load review queue.")); }, [filter]);

  const open = async (appId) => {
    const r = await api.get(`/admin/partner-applications/${appId}`);
    setSelected(r.data);
    setDecision({
      decision: "approve",
      risk_level: "standard",
      assigned_tier: "registered",
      assigned_territory: "",
      reviewer_note: "",
    });
  };

  const decide = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const r = await api.post(
        `/admin/partner-applications/${selected.application.id}/decision`,
        decision,
      );
      if (r.data?.partner) {
        toast.success(`Approved — partner activated as ${r.data.partner.tier}.`);
      } else {
        toast.success(`Decision recorded: ${r.data.new_status}.`);
      }
      setSelected(null);
      await load();
    } catch (e) {
      const raw = e?.response?.data?.detail;
      toast.error(typeof raw === "string" ? raw : "Couldn't record decision.");
    } finally {
      setBusy(false);
    }
  };

  const items = (data?.items || []).filter((row) => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return [row.name, row.email, row.country, row.legal_business_name]
      .filter(Boolean)
      .some((s) => String(s).toLowerCase().includes(q));
  });

  const stats = data?.stats || {};

  return (
    <div data-testid="admin-partners" className="max-w-6xl mx-auto px-6 py-10">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4B878] mb-2 flex items-center gap-2">
          <ShieldCheck size={12} /> HQ · Partner review
        </div>
        <h1 className="text-3xl sm:text-4xl font-semibold text-white tracking-tight">
          Partner applications
        </h1>
        <p className="mt-2 text-sm text-white/60">
          Every application lands here. Approving materializes a partner
          row and elevates the matching user account to the <span className="font-mono text-[#D4B878]">partner</span> role.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
        {[
          { k: "total", label: "Total" },
          { k: "submitted", label: "Submitted" },
          { k: "info_requested", label: "Info req." },
          { k: "approved", label: "Approved" },
          { k: "declined", label: "Declined" },
        ].map((s) => (
          <div key={s.k} data-testid={`admin-partner-stat-${s.k}`} className="border border-white/10 rounded p-4 bg-white/[0.02]">
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/40">{s.label}</div>
            <div className="text-2xl font-semibold mt-1 text-white font-mono">
              {(stats[s.k] || 0).toLocaleString()}
            </div>
          </div>
        ))}
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {["all", "submitted", "info_requested", "approved", "declined"].map((v) => (
          <button
            key={v}
            onClick={() => setFilter(v)}
            data-testid={`admin-partners-filter-${v}`}
            className={`text-xs uppercase tracking-[0.18em] font-mono px-3 py-1.5 rounded-full border transition ${
              filter === v
                ? "border-[#D4B878]/60 bg-[#D4B878]/[0.08] text-white"
                : "border-white/10 bg-white/[0.02] text-white/60 hover:border-white/25"
            }`}
          >
            {v.replace("_", " ")}
          </button>
        ))}
        <div className="flex-1" />
        <div className="relative">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
          <Input placeholder="Search name, email, country…" value={query} onChange={(e) => setQuery(e.target.value)}
                 className="pl-8 w-64 bg-white/5 border-white/10 text-xs" data-testid="admin-partners-search" />
        </div>
        <Button onClick={load} className="rounded-full border border-white/15 bg-transparent text-white text-xs"
                data-testid="admin-partners-refresh">
          <RefreshCw size={12} className="mr-1" /> Refresh
        </Button>
      </div>

      {/* List */}
      <div className="border border-white/10 rounded-lg bg-white/[0.02] divide-y divide-white/5">
        {items.length === 0 && (
          <div data-testid="admin-partners-empty" className="p-10 text-center text-white/40">
            No applications match this filter.
          </div>
        )}
        {items.map((row) => {
          const meta = STATUS_META[row.status] || STATUS_META.submitted;
          return (
            <button
              key={row.id}
              onClick={() => open(row.id)}
              data-testid={`admin-partners-row-${row.id}`}
              className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-white/[0.03] transition"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <div className="text-white font-medium truncate">{row.name}</div>
                  <span className={`text-[9px] uppercase tracking-[0.14em] font-mono px-1.5 py-0.5 rounded border ${meta.tone}`}>
                    {meta.label}
                  </span>
                </div>
                <div className="text-[11px] text-white/50 font-mono flex items-center gap-3 mt-0.5">
                  <span className="flex items-center gap-1"><Mail size={10} /> {row.email}</span>
                  <span className="flex items-center gap-1"><Globe size={10} /> {row.country}</span>
                  <span>{(row.categories || []).join(" · ")}</span>
                </div>
              </div>
              <ArrowUpRight size={14} className="text-white/30" />
            </button>
          );
        })}
      </div>

      {/* Decision drawer */}
      {selected && (
        <div
          data-testid="admin-partner-decision-drawer"
          className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center p-4 overflow-y-auto"
          onClick={() => !busy && setSelected(null)}
        >
          <div
            className="bg-[#141110] border border-white/15 rounded-lg max-w-2xl w-full p-6 space-y-5 my-10"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="text-[10px] uppercase tracking-[0.24em] text-[#D4B878] font-mono flex items-center gap-1">
                  <Users size={11} /> Application
                </div>
                <div className="text-xl font-semibold text-white">{selected.application.name}</div>
                <div className="text-xs text-white/50 font-mono mt-1">
                  {selected.application.email} · {selected.application.country}
                  {selected.application.city && ` · ${selected.application.city}`}
                </div>
              </div>
              <button onClick={() => !busy && setSelected(null)} className="text-white/40 hover:text-white">
                <XIcon size={18} />
              </button>
            </div>

            {/* App details */}
            <div className="grid grid-cols-2 gap-3 text-xs">
              <Detail label="Kind">{selected.application.kind}</Detail>
              <Detail label="Categories">{(selected.application.categories || []).join(", ")}</Detail>
              <Detail label="Business">{selected.application.legal_business_name || "—"}</Detail>
              <Detail label="Reg #">{selected.application.business_registration || "—"}</Detail>
              <Detail label="Portfolio">
                {selected.application.portfolio_url ? (
                  <a href={selected.application.portfolio_url} target="_blank" rel="noreferrer"
                     className="text-[#D4B878] underline break-all">
                    {selected.application.portfolio_url}
                  </a>
                ) : "—"}
              </Detail>
              <Detail label="Founding slot">{selected.application.want_founding_slot ? "Yes" : "No"}</Detail>
              <Detail label="Opt-in public">{selected.application.opt_in_public_profile ? "Yes" : "No"}</Detail>
              <Detail label="Target markets">
                {(selected.application.target_markets || []).join(", ") || "—"}
              </Detail>
            </div>
            {selected.application.experience_summary && (
              <div className="p-3 border border-white/10 rounded text-xs text-white/80 bg-black/20 leading-relaxed">
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1">Experience</div>
                {selected.application.experience_summary}
              </div>
            )}

            {/* Decision form */}
            <div className="pt-4 border-t border-white/10 space-y-3">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4B878] font-mono">Decision</div>
              <div className="grid grid-cols-4 gap-2">
                {[
                  { v: "approve", label: "Approve", tone: "border-[#CCFF00]/50 text-[#CCFF00]" },
                  { v: "info_requested", label: "Info", tone: "border-[#E8D6A3]/40 text-[#E8D6A3]" },
                  { v: "waitlist", label: "Waitlist", tone: "border-white/20 text-white/70" },
                  { v: "decline", label: "Decline", tone: "border-red-400/40 text-red-300" },
                ].map((d) => (
                  <button
                    key={d.v}
                    type="button"
                    onClick={() => setDecision({ ...decision, decision: d.v })}
                    data-testid={`admin-decision-${d.v}`}
                    className={`text-xs uppercase tracking-[0.14em] font-mono px-3 py-2 rounded border transition ${
                      decision.decision === d.v
                        ? `${d.tone} bg-white/[0.04] text-white`
                        : "border-white/10 bg-white/[0.02] text-white/50 hover:border-white/25"
                    }`}
                  >
                    {d.label}
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.2em] text-white/50">Risk level</Label>
                  <select data-testid="admin-decision-risk" value={decision.risk_level}
                          onChange={(e) => setDecision({ ...decision, risk_level: e.target.value })}
                          className="w-full mt-1 bg-white/5 border border-white/10 text-xs rounded px-2 py-1.5 text-white font-mono">
                    {["standard", "elevated", "high"].map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.2em] text-white/50">Tier</Label>
                  <select data-testid="admin-decision-tier" value={decision.assigned_tier}
                          onChange={(e) => setDecision({ ...decision, assigned_tier: e.target.value })}
                          className="w-full mt-1 bg-white/5 border border-white/10 text-xs rounded px-2 py-1.5 text-white font-mono">
                    {["registered", "silver", "gold", "platinum"].map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.2em] text-white/50">Territory</Label>
                  <Input data-testid="admin-decision-territory"
                         value={decision.assigned_territory}
                         onChange={(e) => setDecision({ ...decision, assigned_territory: e.target.value })}
                         placeholder="India — West"
                         className="w-full mt-1 bg-white/5 border-white/10 text-xs" />
                </div>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] text-white/50">Reviewer note (visible to the applicant)</Label>
                <textarea data-testid="admin-decision-note" rows={2}
                          value={decision.reviewer_note}
                          onChange={(e) => setDecision({ ...decision, reviewer_note: e.target.value })}
                          placeholder="Why this decision?"
                          className="w-full mt-1 bg-white/5 border border-white/10 rounded p-2 text-sm text-white placeholder:text-white/30 resize-none" />
              </div>

              <Button onClick={decide} disabled={busy}
                      data-testid="admin-decision-submit"
                      className="w-full rounded-full bg-[#CCFF00] hover:bg-[#B8E600] text-black font-semibold">
                <Check size={13} className="mr-1.5" />
                {busy ? "Recording…" : `Record "${decision.decision}" decision`}
              </Button>
            </div>

            {/* Audit trail */}
            {(selected.audit || []).length > 0 && (
              <div className="pt-4 border-t border-white/10">
                <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 font-mono mb-2">Audit trail</div>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {selected.audit.map((a) => (
                    <div key={a.id} className="text-[11px] text-white/50 font-mono flex items-center gap-2">
                      <Clock size={10} /> {String(a.created_at).slice(0, 19).replace("T", " ")} · <span className="text-[#D4B878]">{a.action}</span> {a.actor_email && `· ${a.actor_email}`}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Detail({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 mb-0.5">{label}</div>
      <div className="text-white/80">{children}</div>
    </div>
  );
}
